import { useEffect } from "react"

import { App, Flex, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Icon from "../../../Icon"
import { isOnboardingFinished } from "../../../Utils/onboarding/isOnboardingFinished"
import { onboardingResumePath } from "../../../Utils/onboarding/productChoice"
import utils, { createAccountStepPath } from "../../../Utils/utils"
import {
  resetShopifyMove,
  selectShopifyMoveOffer,
  selectShopifyMoveReturnUrl
} from "../redux/shopifyMove.slice"
import { moveShopifyStore } from "../redux/shopifyMove.thunks"

import styles from "./ShopifyMoveModals.module.less"

const { Text } = Typography

// Marks *word* segments to stand out against the surrounding secondary-colored copy —
// regular weight, just the default (primary) text color for contrast.
const withEmphasis = (text) =>
  text.split("*").map((part, idx) => (idx % 2 ? <Text key={idx}>{part}</Text> : part))

/**
 * The two popups a movable Shopify store gets after the Shopify OAuth return.
 *
 * A store lives on exactly one business account, so when the one being connected is
 * already taken the backend answers the return call with a `shopify_move` object
 * instead of only a `url`. `move_kind` splits the copy: `same_user` when both accounts
 * belong to the person connecting, `cross_user` when the store sits under someone
 * else's account — the second wording names that owner's email.
 *
 * Mounted app-wide and rendering nothing: both popups are the shared imperative
 * confirm modal (same as the "already linked to another Business Account" help modal),
 * and the result lands in the app's bottom-right notification.
 */
const ShopifyMoveModals = () => {
  const { t } = useTranslation()
  const { modal, notification } = App.useApp()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const offer = useSelector(selectShopifyMoveOffer)
  const returnUrl = useSelector(selectShopifyMoveReturnUrl)
  const isMobile = useSelector((state) => state.nav.isMobile)
  const userInfo = useSelector((state) => state.auth.userInfo)
  const nextCreateAccountStep = useSelector((state) => state.user.nextCreateAccountStep)

  useEffect(() => {
    if (!offer) return undefined

    const isCrossUser = offer?.move_kind === "cross_user"
    const previousAccountName = offer?.previous_business_account?.name
    const targetAccountName = offer?.target_business_account?.name
    const ownerEmail = offer?.previous_business_account?.owner_email
    // The display name is only there when the backend managed to read it from Shopify.
    const storeName = offer?.service_info?.store_name || offer?.service_info?.shop_domain

    const notificationConfig = {
      closeIcon: <Text>{t("Dismiss")}</Text>,
      style: { minWidth: "max-content", width: 475 },
      placement: "bottomRight"
    }

    /**
     * Both buttons leave the OAuth hand-off page. A user still setting an account up
     * goes back to the step they came from; anyone else follows the url the backend
     * gave: `redirect_url` after a move, the return call's own `url` on Cancel.
     */
    const leavePage = (fallbackUrl) => {
      dispatch(resetShopifyMove())

      if (nextCreateAccountStep) {
        navigate(createAccountStepPath[nextCreateAccountStep])
      } else if (!isOnboardingFinished(userInfo) && userInfo?.["next_step"]) {
        navigate(onboardingResumePath(userInfo))
      } else if (fallbackUrl) {
        // Both urls come from the backend and usually point back into this app.
        // Routing those in-app keeps the success notification alive, which a full
        // page load would wipe out; anything off-origin still leaves the SPA.
        let inAppPath = null
        try {
          const target = new URL(fallbackUrl, window.location.origin)
          if (target.origin === window.location.origin) {
            inAppPath = `${target.pathname}${target.search}${target.hash}`
          }
        } catch {
          inAppPath = null
        }

        if (inAppPath) {
          navigate(inAppPath)
        } else {
          utils.goLink(fallbackUrl)
        }
      }
    }

    // Returned to the confirm modal so it keeps its loading state until the request
    // settles, and stays open on failure — the global error notification says why.
    const handleConfirm = () =>
      dispatch(
        moveShopifyStore({
          shopify_store_id: offer?.request_params?.shopify_store_id,
          business_id: offer?.target_business_account?.id
        })
      )
        .unwrap()
        .then((result) => {
          notification.success({
            message: t("Shopify store moved"),
            description: isCrossUser
              ? t(
                  "The store is now connected to the _movedTo_ and disconnected from _movedFrom_ from the _ownerEmail_.",
                  {
                    movedTo: result?.moved_to?.name,
                    movedFrom: result?.moved_from?.name,
                    ownerEmail: result?.moved_from?.owner_email
                  }
                )
              : t("The store is now connected to _movedTo_ and disconnected from _movedFrom_.", {
                  movedTo: result?.moved_to?.name,
                  movedFrom: result?.moved_from?.name
                }),
            ...notificationConfig
          })

          leavePage(result?.redirect_url)
        })

    const showConfirmPopup = () => {
      const confirmText = isCrossUser
        ? t(
            "Are you sure you want to move _storeName_ store from *_ownerEmail_* *_previousAccountName_* Business Account to the current *_targetAccountName_*? This action can't be undone.",
            { storeName, ownerEmail, previousAccountName, targetAccountName }
          )
        : t(
            "Are you sure you want to move the _storeName_ store from your previous *_previousAccountName_* Business Account to the current *_targetAccountName_* Business Account? This action can't be undone.",
            { storeName, previousAccountName, targetAccountName }
          )

      modal.confirm({
        icon: null,
        cancelButtonProps: {
          size: "large"
        },
        okButtonProps: {
          size: "large"
        },
        className: `clear-bg ${styles.offerModal}`,
        centered: !isMobile,
        content: (
          <Flex vertical gap={16}>
            <Icon type="circle_warning" className={styles.icon} style={{ fontSize: 64 }} />
            <Flex vertical gap={8}>
              <Text className={styles.title}>{t("Confirm the move")}</Text>
              <Text type="secondary" className={styles.text}>
                {withEmphasis(confirmText)}
              </Text>
            </Flex>
          </Flex>
        ),
        okText: t("Confirm"),
        cancelText: t("Cancel"),
        onOk: handleConfirm,
        onCancel: () => leavePage(returnUrl)
      })
    }

    const offerPopup = modal.confirm({
      width: 450,
      icon: null,
      centered: !isMobile,
      className: `clear-bg ${styles.offerModal}`,
      content: (
        <Flex vertical gap={16}>
          <Icon type="circle_question" className={styles.icon} style={{ fontSize: 64 }} />
          <Flex vertical gap={8}>
            <Text className={styles.title}>{t("Shopify account already connected")}</Text>
            <Flex vertical gap={20}>
              <Text type="secondary" className={styles.text}>
                {withEmphasis(
                  isCrossUser
                    ? t(
                        "It looks like the Shopify account you're trying to connect is already linked to another *_previousAccountName_* Business Account to the *_ownerEmail_*.",
                        { previousAccountName, ownerEmail }
                      )
                    : t(
                        "It looks like the Shopify account you're trying to connect is already linked to your another *_previousAccountName_* Business Account.",
                        { previousAccountName }
                      )
                )}
              </Text>
              <Text type="secondary" className={styles.text}>
                {t(
                  "Unfortunately, you cannot connect the same Shopify account to multiple Chargeback Business Accounts."
                )}
              </Text>
              <Text type="secondary" className={styles.text}>
                {t(
                  "You can either move the Shopify connection to the account you're currently setting up, which will disconnect it from the previous account, or cancel and log in to the previous account to continue the setup there."
                )}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      ),
      okText: t("Move"),
      cancelText: t("Cancel"),
      okButtonProps: { size: "large" },
      cancelButtonProps: { size: "large" },
      onOk: showConfirmPopup,
      onCancel: () => leavePage(returnUrl)
    })

    return () => offerPopup.destroy()
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [offer])

  return null
}

export default ShopifyMoveModals
